/* fs-hero.js — the hero status readout.
     17:55PM        local time, ticking
     H17 L10        today's high / low in °C
     WIND AND RAIN  current conditions
     F. SPORT 2026  fixed

   Location with no permission prompt: the browser's IANA timezone names a city
   ("Pacific/Auckland" -> "Auckland") which goes straight to the weather API as
   a place query. No geolocation dialog, no IP service, no second round trip.

   The two weather lines stay HIDDEN unless real, validated data arrives, so the
   block never shows invented or wrong weather. Clock and wordmark always render.

   PROVIDER: WeatherAPI.com (swapped from Open-Meteo 2026-09-04, whose free tier
   is non-commercial only and so could not ship). Free plan permits commercial
   use, 100k calls/month. The KEY below is visible in page source — unavoidable
   for a browser-side call; if it is ever abused the weather lines just hide.

   THE SANITY CHECK IS THE IMPORTANT PART. Place-name search is fuzzy and fails
   SILENTLY WITH CONFIDENT WRONG DATA rather than erroring. Measured 2026-09-04:
     Europe/Kiev    -> "Kieve, GERMANY"        (old systems still report Kiev;
                                                modern ones report Europe/Kyiv,
                                                which resolves correctly)
     Pacific/Chatham-> "Chatham, UNITED KINGDOM"
     Pacific/Truk   -> "Kon Truk, VIETNAM"
   Eleven other zones resolved correctly, including renamed aliases (Calcutta,
   Katmandu). So every response is checked against the visitor's own clock:
   accept if the resolved tz_id matches the browser's timezone, or if the
   resolved place's local wall time is within TOLERANCE_MIN of the visitor's.
   Otherwise treat it as a miss and show nothing. Kieve is only 1 hour from
   Kyiv, which is why the tolerance is tight rather than generous. */
(function () {
  var KEY = '8acbced15e0d4b76af544658260409';
  /* v3: cache shape changed again with the validation work. Entries from
     earlier versions hold Open-Meteo WMO codes and are ignored, not
     mistranslated. */
  var CACHE_KEY = 'fs-hero-wx3';
  var CACHE_MS = 30 * 60 * 1000;
  var TOLERANCE_MIN = 35;

  /* WeatherAPI condition codes -> the mockup's short uppercase vocabulary.
     Explicit rather than range-based: unlike WMO codes these are not contiguous
     by category, so ranges would silently mis-bucket. */
  var CODES = {
    1000: 'CLEAR', 1003: 'PARTLY CLOUDY', 1006: 'CLOUDY', 1009: 'OVERCAST',
    1030: 'FOG', 1135: 'FOG', 1147: 'FOG',
    1063: 'RAIN', 1180: 'RAIN', 1183: 'RAIN', 1186: 'RAIN',
    1189: 'RAIN', 1192: 'RAIN', 1195: 'RAIN',
    1150: 'DRIZZLE', 1153: 'DRIZZLE', 1168: 'DRIZZLE', 1171: 'DRIZZLE',
    1198: 'FREEZING RAIN', 1201: 'FREEZING RAIN',
    1069: 'SLEET', 1204: 'SLEET', 1207: 'SLEET', 1249: 'SLEET', 1252: 'SLEET',
    1066: 'SNOW', 1114: 'SNOW', 1117: 'SNOW', 1210: 'SNOW', 1213: 'SNOW',
    1216: 'SNOW', 1219: 'SNOW', 1222: 'SNOW', 1225: 'SNOW',
    1255: 'SNOW SHOWERS', 1258: 'SNOW SHOWERS',
    1240: 'SHOWERS', 1243: 'SHOWERS', 1246: 'SHOWERS',
    1237: 'HAIL', 1261: 'HAIL', 1264: 'HAIL',
    1087: 'THUNDERSTORM', 1273: 'THUNDERSTORM', 1276: 'THUNDERSTORM',
    1279: 'THUNDERSTORM', 1282: 'THUNDERSTORM'
  };

  /* Fallback for an unmapped code. Order matters: thunder before rain, snow
     shower before shower. '' leaves the line hidden — never a guess. */
  function conditionFromText(text) {
    var t = (text || '').toLowerCase();
    if (!t) return '';
    if (t.indexOf('thunder') !== -1) return 'THUNDERSTORM';
    if (t.indexOf('ice pellet') !== -1) return 'HAIL';
    if (t.indexOf('sleet') !== -1) return 'SLEET';
    if (t.indexOf('snow') !== -1) return t.indexOf('shower') !== -1 ? 'SNOW SHOWERS' : 'SNOW';
    if (t.indexOf('drizzle') !== -1) return 'DRIZZLE';
    if (t.indexOf('shower') !== -1) return 'SHOWERS';
    if (t.indexOf('rain') !== -1) return 'RAIN';
    if (t.indexOf('fog') !== -1 || t.indexOf('mist') !== -1) return 'FOG';
    if (t.indexOf('overcast') !== -1) return 'OVERCAST';
    if (t.indexOf('cloud') !== -1) return 'CLOUDY';
    if (t.indexOf('sunny') !== -1 || t.indexOf('clear') !== -1) return 'CLEAR';
    return '';
  }

  function conditionText(code, text) {
    return CODES[code] || conditionFromText(text);
  }

  function timezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) { return ''; }
  }

  function cityFromTimezone(tz) {
    if (!tz || tz.indexOf('/') === -1) return '';
    return tz.split('/').pop().replace(/_/g, ' ');
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* Wall-clock readings compared as minutes on a common scale, so a comparison
     across a date boundary (23:50 there / 00:10 here) is 20 minutes, not 23
     hours. Date.UTC is used purely as arithmetic — no zone conversion happens. */
  function wallMinutes(y, mo, d, h, mi) {
    return Date.UTC(y, mo - 1, d, h, mi) / 60000;
  }

  /* "2026-09-04 19:51" -> minutes. Parsed by hand: new Date() on that format is
     not reliable across engines. Returns null if it does not parse. */
  function parseLocaltime(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s || '');
    if (!m) return null;
    return wallMinutes(+m[1], +m[2], +m[3], +m[4], +m[5]);
  }

  function browserWallMinutes() {
    var n = new Date();
    return wallMinutes(n.getFullYear(), n.getMonth() + 1, n.getDate(),
                       n.getHours(), n.getMinutes());
  }

  /* True when the place the API resolved is plausibly where the visitor is. */
  function locationLooksRight(loc) {
    if (!loc) return false;
    if (loc.tz_id && loc.tz_id === timezone()) return true;
    var there = parseLocaltime(loc.localtime);
    if (there === null) return false;
    return Math.abs(there - browserWallMinutes()) <= TOLERANCE_MIN;
  }

  function readCache() {
    try {
      var raw = window.localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj || Date.now() - obj.at > CACHE_MS) return null;
      if (obj.tz !== timezone()) return null;
      return obj.data;
    } catch (e) { return null; }
  }

  function writeCache(data) {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify({
        at: Date.now(), tz: timezone(), data: data
      }));
    } catch (e) { /* private mode, quota — not worth handling */ }
  }

  /* The only provider-specific function. Resolves to
     { high, low, code, text, wind } or rejects. Swap the body to change
     provider — but keep a locationLooksRight()-equivalent check. */
  function fetchWeather(city) {
    var url = 'https://api.weatherapi.com/v1/forecast.json'
      + '?key=' + encodeURIComponent(KEY)
      + '&days=1&aqi=no&alerts=no'
      + '&q=' + encodeURIComponent(city);

    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('weather ' + r.status);
      return r.json();
    }).then(function (w) {
      var day = w && w.forecast && w.forecast.forecastday
        && w.forecast.forecastday[0] && w.forecast.forecastday[0].day;
      var cur = w && w.current;
      if (!day || !cur) throw new Error('unexpected payload');
      if (!locationLooksRight(w.location)) {
        throw new Error('resolved elsewhere: ' + (w.location && w.location.name));
      }
      return {
        high: Math.round(day.maxtemp_c),
        low: Math.round(day.mintemp_c),
        code: cur.condition && cur.condition.code,
        text: cur.condition && cur.condition.text,
        wind: cur.wind_kph
      };
    });
  }

  function renderClock(el) {
    var now = new Date();
    var h = now.getHours();
    // Reproduces the mockup exactly: 24-hour clock with an AM/PM marker.
    el.textContent = pad(h) + ':' + pad(now.getMinutes()) + (h < 12 ? 'AM' : 'PM');
  }

  function applyWeather(els, data) {
    if (!data || typeof data.high !== 'number' || isNaN(data.high)) return;

    els.temps.textContent = 'H' + pad(data.high) + ' L' + pad(data.low);
    els.temps.hidden = false;

    var text = conditionText(data.code, data.text);
    // Matches the mockup's "<something> AND RAIN" phrasing when it's blowing.
    if (text && data.wind >= 25) text = 'WIND AND ' + text;
    if (text) {
      els.cond.textContent = text;
      els.cond.hidden = false;
    }
  }

  function init() {
    var hero = document.querySelector('.section-background-video .text-overlay');
    if (!hero || hero.querySelector('.fs-hero-status')) return;

    var block = document.createElement('div');
    block.className = 'fs-hero-status';

    function line(cls, text, hide) {
      var d = document.createElement('div');
      d.className = 'fs-hero-status__line' + (cls ? ' ' + cls : '');
      if (text) d.textContent = text;
      if (hide) d.hidden = true;
      block.appendChild(d);
      return d;
    }

    var els = {
      clock: line('fs-hero-status__clock', '', false),
      temps: line('fs-hero-status__temps', '', true),
      cond: line('fs-hero-status__cond', '', true)
    };
    line('fs-hero-status__mark', 'F. Sport 2026', false);

    hero.appendChild(block);

    renderClock(els.clock);
    window.setInterval(function () { renderClock(els.clock); }, 15000);

    var cached = readCache();
    if (cached) { applyWeather(els, cached); return; }

    var city = cityFromTimezone(timezone());
    if (!city || typeof fetch !== 'function') return;

    fetchWeather(city).then(function (data) {
      applyWeather(els, data);
      writeCache(data);
    }).catch(function () {
      /* Leave the weather lines hidden rather than showing wrong numbers. */
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
